export function delay(timeMs: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, timeMs));
}

export function exists<T>(v: T | undefined | null): v is T {
  return v != null;
}

export class UnreachableError extends Error {
  constructor(v: never) {
    super(`UnreachableError: ${v}`);
  }
}

/**
 * Formats a timestamp (in seconds) into a relative time string
 **/
export function relativeTime(timestamp: number): string {
  const relativeTimePeriods = [
    [31536000, 'year'],
    [604800, 'week'],
    [86400, 'day'],
    [3600, 'hour'],
    [60, 'minute'],
  ];

  const seconds = +new Date() / 1000 - timestamp;
  for (let [secondsPer, name] of relativeTimePeriods) {
    if (seconds >= (secondsPer as number)) {
      const amount = Math.floor(seconds / +secondsPer);
      return `${amount} ${name}${amount - 1 ? 's' : ''} ago`;
    }
  }
  return 'Just now';
}

/** For use with react keys */
export function keyOfObject(o: object) {
  if (!objectKeys.has(o)) {
    objectKeys.set(o, `${objectKeyCounter++}`);
  }
  return objectKeys.get(o)!;
}
const objectKeys = new WeakMap<object, string>();
let objectKeyCounter = 0;

/**
 * Helper for creating a sort comparator function that can be used inline
 * with Array.sort() in ascending order.
 *
 * @param f Function that returns a key to use for sorting a given array element
 * @returns Sort comparator function
 */
export function sortComparatorAsc<T>(
  f: (value: T) => number,
): (a: T, b: T) => number;
export function sortComparatorAsc<T>(
  f: (value: T) => string,
): (a: T, b: T) => number;
export function sortComparatorAsc<T>(
  f: (value: T) => boolean,
): (a: T, b: T) => number;
export function sortComparatorAsc<T>(
  f: (value: T) => string | number | boolean,
) {
  return (a: T, b: T) => {
    const aKey = f(a);
    const bKey = f(b);
    if (typeof aKey === 'string' && typeof bKey === 'string') {
      return aKey.localeCompare(bKey);
    }
    if (typeof aKey === 'number' && typeof bKey === 'number') {
      return aKey - bKey;
    }
    if (typeof aKey === 'boolean' && typeof bKey === 'boolean') {
      return Number(aKey) - Number(bKey);
    }
    throw new Error('Sort types did not match');
  };
}

/**
 * Helper for creating a sort comparator function that can be used inline
 * with Array.sort() in descending order.
 *
 * @param f Function that returns a key to use for sorting a given array element
 * @returns Sort comparator function
 */
export function sortComparatorDesc<T>(
  f: (value: T) => number,
): (a: T, b: T) => number;
export function sortComparatorDesc<T>(
  f: (value: T) => string,
): (a: T, b: T) => number;
export function sortComparatorDesc<T>(
  f: (value: T) => boolean,
): (a: T, b: T) => number;
export function sortComparatorDesc<T>(
  f: (value: T) => string | number | boolean,
) {
  return (a: T, b: T) => {
    const aKey = f(a);
    const bKey = f(b);
    if (typeof aKey === 'string' && typeof bKey === 'string') {
      return -aKey.localeCompare(bKey);
    }
    if (typeof aKey === 'number' && typeof bKey === 'number') {
      return bKey - aKey;
    }
    if (typeof aKey === 'boolean' && typeof bKey === 'boolean') {
      return Number(bKey) - Number(aKey);
    }
    throw new Error('Sort types did not match');
  };
}

export const pluralise = (
  n: number,
  singular: string,
  plural: string = `${singular}s`,
) => (n === 1 ? singular : plural);

export function capitalizeFirstLetter(string: string) {
  return string.charAt(0).toUpperCase() + string.slice(1);
}

export const unique = <T>(): ((item: T) => boolean) => {
  const set = new Set<T>();
  return (item: T) => {
    if (set.has(item)) {
      return false;
    }
    set.add(item);
    return true;
  };
};

export const lazy = <T>(f: () => T): (() => T) => {
  let value: T | undefined;
  return () => {
    if (value === undefined) {
      value = f();
    }
    return value;
  };
};

export const lazyAsync = <T>(f: () => Promise<T>): (() => Promise<T>) => {
  let promise: Promise<T> | undefined;
  return async () => {
    if (promise == null) {
      promise = f();
    }
    return await promise;
  };
};

export type MapValue<T extends Map<any, any>> = T['get'] extends (
  key: any,
) => infer V
  ? Exclude<V, undefined>
  : never;

export function formatDate(
  timestamp: number,
  format: string = 'dd/mm/yyyy HH:MM:SS.mmm',
) {
  const date = new Date(timestamp);

  const replacements: Record<
    'dd' | 'mm' | 'yyyy' | 'HH' | 'MM' | 'SS' | 'mmm',
    string
  > = {
    dd: String(date.getDate()).padStart(2, '0'),
    mm: String(date.getMonth() + 1).padStart(2, '0'), // Months are zero-based
    yyyy: String(date.getFullYear()),
    HH: String(date.getHours()).padStart(2, '0'),
    MM: String(date.getMinutes()).padStart(2, '0'),
    SS: String(date.getSeconds()).padStart(2, '0'),
    mmm: String(date.getMilliseconds()).padStart(3, '0'),
  };

  return format.replace(
    /dd|mm|yyyy|HH|MM|SS|mmm/g,
    (match) => replacements[match as keyof typeof replacements],
  );
}

export function getProjectId(projectToken: string): string {
  return projectToken.split('_')[0].substring(1);
}
